import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { isAuthPage } from 'src/app/core/interceptors/auth-error.util';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ImageService } from '../imageService/image.service';
import { FilterService } from 'src/app/tasks/services/filterService/filter.service';
import { ReportDraftService } from 'src/app/projects/services/report-draft.service';
import { NavigationService } from 'src/app/shared/services/navigationService/navigation.service';
import { LegacyQueueMigration } from 'src/app/core/offline/legacy-queue-migration.service';
import { ConflictService } from 'src/app/core/offline/conflict.service';
import { OfflineAvailabilityService } from 'src/app/core/offline/offline-availability.service';
import { OfflineDeviceDataService } from 'src/app/core/offline/offline-device-data.service';
import { PushSubscriptionService } from 'src/app/notifications/services/push-subscription.service';
import { DatabaseService } from '../databaseService/database.service';

// F3: zentrale Stelle für alles, was beim Beginn/Ende einer Anmeldung im selben Tab passieren muss.
// Kein Zirkelbezug: AuthService, ImageService, FilterService und
// NavigationService hängen nicht von SessionService ab (verifiziert), daher kann SessionService sie
// gefahrlos injizieren.
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  // Router, Toastr und Translate erst bei Bedarf auflösen (TranslateHttpLoader hängt an HttpClient → Interceptor)
  private readonly injector = inject(Injector);
  // Gescheiterter Refresh: alle Wartenden bekommen dasselbe Ergebnisobjekt, abgemeldet wird nur einmal
  private readonly handledRejections = new WeakSet<object>();

  constructor(
    private authService: AuthService,
    private imageService: ImageService,
    private filterService: FilterService,
    private navigationService: NavigationService,
    private reportDraftService: ReportDraftService,
  ) {
    // FE-3: ein anderer Tab hat sich abgemeldet (nur mit AUTH_V2; im Legacy-Modus gibt es keinen Kanal).
    // Dieser Tab räumt gleich auf und geht zum Login, ohne erneuten Broadcast und ohne zweiten Server-Logout.
    this.authService.onRemoteLogout$.subscribe(() => this.endSession({ navigate: true, broadcast: false }));
    // FE-G1 (Security-Gate Offline E2): another user signed in in another tab. This tab ends its session (in-memory
    // state, login page) without new broadcast and without touching localStorage: it belongs to the new user now.
    // a tab without user takes over a session (refresh, broadcast): data removed at the last logout may be written again
    this.authService.onSessionTakenOver$.subscribe((userId) => this.injector.get(OfflineAvailabilityService).thawUser(userId));
    this.authService.onSessionSuperseded$.subscribe(() => this.endSession({ broadcast: false, superseded: true }));
    // Push-Service früh erzeugen: er führt das aktuelle Abo mit, damit der Logout nicht warten muss
    try {
      this.injector.get(PushSubscriptionService);
    } catch {
      // ohne Service Worker nicht nötig
    }
  }

  // Vor einer neuen Anmeldung auf einem geteilten Gerät: Bildspeicher/Kontext einer vorigen Session
  // entfernen (z.B. nach abgelaufenem Token ohne expliziten Logout).
  // W1: userId des neu angemeldeten Users (LoginComponent/SignupComponent kennen ihn aus der
  // Server-Antwort, bevor storeSession() den Token setzt). Weicht er vom zuletzt angemeldeten User auf
  // diesem Gerät ab, gehören dessen Filter (FilterService) und Origin-Pages (NavigationService) nicht dem
  // neuen User - beide werden geleert. Derselbe User (z.B. nach Logout/Login) behält sie (0.6).
  beginSession(userId: string | null = null): void {
    if (userId !== null) {
      const lastUserId = this.authService.getLastSessionUserId();
      if (lastUserId !== null && lastUserId !== userId) {
        this.filterService.clearAllFilterStates();
        this.navigationService.clearAllOriginPages();
      }
      this.authService.setLastSessionUserId(userId);
      // Gate E-2 b: this user is active on the device; data of users absent for 30 days expires
      void this.injector.get(OfflineDeviceDataService).markActiveAndExpire(userId);
      // removed his offline data at the last logout (E-2 a): snapshots and plans may be written again (Rev1 review H5)
      this.injector.get(OfflineAvailabilityService).thawUser(userId);
    }
    this.imageService.clearImages();
    this.imageService.setContext(null);
    // Offline E2 step 3 (fix round 1, H1): «Kopieren» texts of the old queue never outlive their session
    this.injector.get(LegacyQueueMigration).reset();
    // a sign-in in the same tab without logout (login page has no guard): nothing of the old session stays
    this.clearSessionState();
    void this.clearApiCache();
  }

  // Zentraler Ablauf für das Ende einer Anmeldung: Logout (Standard, mit Navigation zum Login über
  // AuthService.logout()) und ungültige Sitzung bei 401 (navigate: false; der Interceptor leitet selbst
  // mit Rücksprung-URL um). Entwürfe und Offline-Warteschlange in IndexedDB bleiben erhalten.
  // FE-3 (nur v2): ein expliziter Logout (mit Navigation, broadcast nicht false) widerruft die Sitzung auch auf dem
  // Server und meldet die übrigen Tabs ab. broadcast: false = Empfang eines Logouts aus einem anderen Tab.
  // superseded (FE-G1): another user signed in in another tab; this tab ends its session with navigation to the login,
  // but leaves the auth data in localStorage alone (they belong to the new user) and calls no server logout.
  // A superseded tab (AuthService.superseded) always ends this way: a logout or 401 there must neither clear the
  // storage nor revoke the session of the new user.
  endSession(options: { navigate?: boolean; broadcast?: boolean; superseded?: boolean } = {}): void {
    const superseded = options.superseded === true || this.authService.superseded === true;
    const explicitV2Logout =
      options.navigate !== false && options.broadcast !== false && !superseded && this.authService.mode === 'v2';
    // Vor dem lokalen Aufräumen: die Anfrage startet sofort, danach fehlt authMode
    // Push dieses Geräts zuerst abmelden: die DELETE-Anfrage wird sofort abgesendet (nicht abgewartet, Token jetzt
    // festhalten, bis 3 s), danach im selben Tick der Widerruf der Sitzung. Der Widerruf wird nie verzögert.
    if (!superseded && options.navigate !== false && options.broadcast !== false) {
      this.unregisterPush(this.authService.getToken());
    }
    if (explicitV2Logout) void this.authService.logoutRemote();
    this.imageService.clearImages();
    this.imageService.setContext(null);
    this.injector.get(LegacyQueueMigration).reset();
    this.clearSessionState();
    void this.clearApiCache(); // gilt für Logout und für den 401-Pfad aus dem Interceptor (navigate: false)
    if (superseded) {
      if (options.superseded) this.leaveSupersededTab();
      // «Abmelden» in a tab that was superseded before: only to the login, the storage belongs to the new user (NA-48)
      else if (options.navigate !== false) void this.injector.get(Router).navigate(['/auth/login']);
    } else if (options.navigate === false) {
      // N1: derselbe Ablauf wie beim expliziten Logout (Auth-Daten + Chatbot-Kontext), nur ohne Navigation.
      this.authService.clearSessionData();
    } else {
      this.authService.logout();
    }
    if (explicitV2Logout) this.authService.broadcastLogout();
  }

  /** Push dieses Geräts beim Logout kündigen; scheitert nie und bremst den Logout nicht (kein SW, offline). */
  private unregisterPush(token: string | null): void {
    try {
      void this.injector.get(PushSubscriptionService).unregisterForLogout(token);
    } catch (error) {
      console.warn('[SessionService] push cleanup skipped', error instanceof Error ? error.name : 'unknown');
    }
  }

  /**
   * Der Server lehnt die Sitzung ab (OAuth Phase 1): Refresh gescheitert (401, 404) oder ein frisch geholtes Token
   * wird erneut mit 401 beantwortet. Gleicher Ablauf für Interceptor, Offline-Sync und WebSocket: endSession ohne
   * eigene Navigation (Bild-Kontext, SW-Cache, Auth-Daten; Offline-Warteschlange bleibt), Meldung und Login
   * mit Rücksprung-URL für `userId`.
   * @param sentToken  abgelehntes Token (null, wenn keines im Speicher lag)
   * @param once       Ergebnis des Refresh: mehrere Aufrufer mit demselben Ergebnis melden nur einmal ab. Ohne `once`
   *                   gilt wie beim 401-Pfad: nur, solange `sentToken` noch das aktuelle Token ist.
   */
  endRejectedSession(options: { sentToken: string | null; userId: string | null; alertKey: string; once?: object }): void {
    const { sentToken, userId, alertKey, once } = options;
    const current = this.authService.getToken();
    if (once) {
      if (this.handledRejections.has(once)) return;
      this.handledRejections.add(once);
      // Inzwischen (z.B. in einem anderen Tab) neu angemeldet: nicht abmelden
      if (current !== null && current !== sentToken) return;
    } else if (current !== sentToken) {
      return;
    }
    const router = this.injector.get(Router);
    // Ziel einer laufenden Navigation bevorzugen, sonst aktuelle Seite; nie von einer Auth-Seite auf sich selbst
    const url = router.currentNavigation()?.finalUrl?.toString() ?? router.url;
    const onAuthPage = isAuthPage(url);
    if (!onAuthPage) this.authService.rememberRedirect(url, userId);
    this.endSession({ navigate: false });
    if (onAuthPage) return;
    const message = this.injector.get(TranslateService).instant(alertKey);
    this.injector.get(ToastrService).info(message, '', { positionClass: 'toast-top-center' });
    void router.navigate(['/auth/login']);
  }

  /** test seam: a full reload drops every in-memory state of every page (FE-G1 fix round 1) */
  reloadToLogin: () => void = () => window.location.replace('/auth/login');

  /**
   * FE-G1 fix round 1: the superseded tab goes to the login. The unsaved-changes guard and the beforeunload prompt let
   * it pass (AuthService.superseded). Should the navigation still fail, the tab reloads; the lock in AuthService (no
   * user, no token, no refresh, no writes) holds either way.
   */
  private leaveSupersededTab(): void {
    const toastr = this.injector.get(ToastrService);
    toastr.info(this.injector.get(TranslateService).instant('session.superseded'), '', { positionClass: 'toast-top-center' });
    this.injector
      .get(Router)
      .navigate(['/auth/login'])
      .then(
        (done) => {
          if (!done) this.reloadToLogin();
        },
        () => this.reloadToLogin(),
      );
  }

  /**
   * Security-Gate Offline E2 (FE-G6, FE-G10): state that is not bound to a user must not outlive its session. Conflict
   * dialog (global, outside the router outlet), unsaved report drafts, checkmarks «offline verfügbar» (the login loads
   * those of the new user), and the device-wide picture slots of the camera (old Dexie tables without userId). The
   * outbox and all other offline data stay (spec §6).
   */
  private clearSessionState(): void {
    this.injector.get(ConflictService).close();
    this.reportDraftService.clearAll();
    this.injector.get(OfflineAvailabilityService).clearAvailable();
    const db = this.injector.get(DatabaseService);
    void Promise.all([db.deleteEditPicture(), db.deleteEditUploadedImages()]).catch((error: unknown) =>
      console.warn('[SessionService] picture slots not cleared', error instanceof Error ? error.name : 'unknown'),
    );
  }

  // 0.9: Antworten im SW-Daten-Cache sind nicht pro User getrennt. Seit E2-Nacharbeit (T29) gibt es keine dataGroup
  // mehr; das Leeren entfernt nur Reste älterer SW-Versionen (bei App-Start, Anmeldung, Abmeldung), bis der Worker
  // sie selbst bereinigt (cleanupCaches).
  // Einträge löschen statt Caches: der Worker hält offene Cache-Objekte; ein gelöschter Cache-Name
  // würde von ihm bis zum Neustart weiter benutzt.
  async clearApiCache(): Promise<void> {
    if (typeof caches === 'undefined') return;
    try {
      const names = await caches.keys();
      for (const name of names.filter((n) => n.startsWith('ngsw:') && n.includes(':data:'))) {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        await Promise.all(requests.map((request) => cache.delete(request)));
      }
    } catch (error) {
      console.warn('[SessionService] API-Cache konnte nicht geleert werden', error);
    }
  }
}
